// =========================================================================================@@
// Last Updated Date: Mar 30, 2023
// Last Updated By: Steven
// Status Level: 3
// ===========================================================================================

import React, { useContext } from 'react'
import { Modal, View } from 'oio-react'
import Helmet from 'react-helmet'
import { Switch, useRouteMatch } from 'react-router-dom'
import { MoreIcon } from 'assets/icons'
import { useOrganization } from 'src/core/graphql/hooks'
import ObjectMoreButton from 'src/sites/kits/Object/components/MoreButton'
import ObjectStatusBanner from 'src/sites/kits/Object/components/StatusBanner'
import ContentView from 'src/sites/kits/UI/Content/View'
import { EmptyContentBlock, Footer } from 'src/sites/kits/UI'
import VisualEditor from 'src/sites/kits/Object/components/VisualEditor'
import ModalRoute from 'src/sites/kits/Utils/ModalRoute'
import { InitiativeHierarchyContext, withInitiativeHierarchyProvider }
   from 'src/sites/kits/Utils/InitiativeHierarchy'
import PageUIContext from 'src/sites/kits/Utils/PageUIContext'
import { useGlobalState } from 'src/sites/state'

const CustomPage = () => {
   const match = useRouteMatch()
   const { theme } = useGlobalState()
   const { initiative: page, initiativeLoading } = useContext(InitiativeHierarchyContext)
   const { organization } = useOrganization()
   const { currentUserCanEdit } = organization

   if (initiativeLoading) {
      return null
   }

   const isEmptyPage = page.body.elements.length === 0

   return (
      <>
         <Helmet title={`${page.name} | ${organization.name}`} />
         <View
            position="relative"
            marginTop={theme.viewportMarginTop}
            float="left"
            width="100%">
            <ObjectStatusBanner />
            <View
               position="fixed"
               bottom="0px"
               right="0px"
               margin="30px"
               zIndex="2">
               <ObjectMoreButton
                  editUrl={`${match.url}/-/edit`}
                  popoverAnchorOriginVertical="bottom"
                  showInitiativeName
               />
            </View>
            {isEmptyPage && (
               <EmptyContentBlock
                  message={(
                     <div>
                        This page currently has no contents.
                        {page.currentUserCanEdit && (
                           <>
                              <br />
                              Click the
                              <View
                                 display="inline-flex"
                                 justifyContent="center"
                                 alignItems="center"
                                 width="20px"
                                 height="20px"
                                 margin="2px 5px 0px 5px"
                                 backgroundColor="#e5e5e5"
                                 borderRadius="50%">
                                 <MoreIcon width="12px" height="12px" />
                              </View>
                              button in the bottom right corner of the page to edit.
                           </>
                        )}
                     </div>
                  )}
               />
            )}
            <View
               float="left"
               width="100%"
               minHeight="90vh"
               paddingHorizontal="350px[f]"
               paddingVertical="30px[a-b] 80px[c-f]">
               <PageUIContext.Provider
                  value={{
                     contentMaxWidth: '750px[a-e]',
                     contentPaddingLeft: `
                        ${theme.tmpViewportPaddingA}[a]
                        ${theme.tmpViewportPaddingB}[b]
                        ${theme.tmpViewportPaddingC}[c]
                     `,
                     contentPaddingRight: `
                        ${theme.tmpViewportPaddingA}[a]
                        ${theme.tmpViewportPaddingB}[b]
                        ${theme.tmpViewportPaddingC}[c]
                     `,
                     contentPaddingVertical: '12px'
                  }}>
                  <ContentView elements={page.body.elements} />
               </PageUIContext.Provider>
            </View>
         </View>
         <Footer />
         {currentUserCanEdit && (
            <Switch>
               <ModalRoute path={`${match.path}/-/edit`}>
                  {({ open, onCloseComplete, onCloseTrigger }) => (
                     <Modal
                        borderRadius="6px"
                        width="100%"
                        height="100%"
                        onCloseComplete={onCloseComplete}
                        onCloseTrigger={onCloseTrigger}
                        openAnimationName="fadeIn"
                        open={open}>
                        <VisualEditor returnUrl={match.url} />
                     </Modal>
                  )}
               </ModalRoute>
            </Switch>
         )}
      </>
   )
}

export default withInitiativeHierarchyProvider({ slugParam: 'initiativeSlug' })(CustomPage)
